<template>
  <div>
    <!-- Hero -->
    <div class="catalog-hero mb-1">
      <div class="catalog-hero__bg"/>
      <div class="catalog-hero__content">
        <div>
          <h3 class="catalog-hero__title mb-25">{{ msg('Catalog') }}</h3>
          <p class="catalog-hero__subtitle mb-0">{{ msg('Manage catalog items and track orders') }}</p>
        </div>
        <div class="d-flex flex-wrap mt-1" style="gap:10px">
          <div class="catalog-hero__stat">
            <feather-icon icon="PackageIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.totalItems }}</span> {{ msg('items') }}
          </div>
          <div class="catalog-hero__stat catalog-hero__stat--success">
            <feather-icon icon="CheckCircleIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.activeItems }}</span> {{ msg('active') }}
          </div>
          <div class="catalog-hero__stat catalog-hero__stat--info">
            <feather-icon icon="ShoppingCartIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.totalOrders }}</span> {{ msg('orders') }}
          </div>
          <div class="catalog-hero__stat catalog-hero__stat--warning">
            <feather-icon icon="ClockIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.pendingOrders }}</span> {{ msg('pending') }}
          </div>
          <div class="catalog-hero__stat catalog-hero__stat--success">
            <feather-icon icon="DollarSignIcon" size="14"/>
            <span class="font-weight-bolder">{{ stats.paidOrders }}</span> {{ msg('paid') }}
          </div>
        </div>
      </div>
      <svg class="catalog-hero__illustration" viewBox="0 0 120 140" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="15" y="10" width="70" height="90" rx="8" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.2)" stroke-width="1.5"/>
        <rect x="25" y="25" width="20" height="20" rx="4" fill="rgba(255,255,255,.18)"/>
        <rect x="50" y="25" width="28" height="3" rx="1.5" fill="rgba(255,255,255,.35)"/>
        <rect x="50" y="32" width="20" height="2.5" rx="1.25" fill="rgba(255,255,255,.2)"/>
        <rect x="25" y="55" width="20" height="20" rx="4" fill="rgba(255,255,255,.18)"/>
        <rect x="50" y="55" width="28" height="3" rx="1.5" fill="rgba(255,255,255,.35)"/>
        <rect x="50" y="62" width="20" height="2.5" rx="1.25" fill="rgba(255,255,255,.2)"/>
        <circle cx="100" cy="20" r="8" fill="rgba(255,255,255,.07)"/>
        <circle cx="105" cy="85" r="6" fill="rgba(255,255,255,.05)"/>
      </svg>
    </div>

    <!-- Tabs -->
    <div class="cm-nav mb-2">
      <div class="cm-nav__tabs">
        <button class="cm-nav__tab" :class="{ 'cm-nav__tab--active': activeTab === 0 }" @click="activeTab = 0">
          <feather-icon icon="PackageIcon" size="16"/>
          <span>{{ msg('Items') }}</span>
          <span v-if="allItems.length" class="cm-nav__tab-count">{{ allItems.length }}</span>
        </button>
        <button class="cm-nav__tab" :class="{ 'cm-nav__tab--active': activeTab === 1 }" @click="activeTab = 1">
          <feather-icon icon="ShoppingCartIcon" size="16"/>
          <span>{{ msg('Orders') }}</span>
          <span v-if="allOrders.length" class="cm-nav__tab-count">{{ allOrders.length }}</span>
        </button>
      </div>
    </div>

    <b-tabs v-model="activeTab" content-class="mt-0" nav-class="d-none">
      <!-- ─── Items Tab ─── -->
      <b-tab>
        <div class="cm-filter mb-1">
          <div class="cm-filter__search">
            <feather-icon icon="SearchIcon" size="15" class="cm-filter__search-icon"/>
            <b-form-input v-model="itemSearch" class="cm-filter__search-input" :placeholder="msg('Search items...')" debounce="300"/>
          </div>
          <div class="cm-filter__divider"/>
          <div class="cm-filter__select">
            <v-select v-model="itemFilterBranch" :options="branchOptions" :reduce="b=>b.id" label="name"
                      :placeholder="msg('All branches')" :clearable="true" class="cm-filter__vs"/>
          </div>
          <div class="cm-filter__select cm-filter__select--sm">
            <v-select v-model="itemFilterActive" :options="activeOptions" :reduce="s=>s.value" label="label"
                      :placeholder="msg('All')" :clearable="true" class="cm-filter__vs"/>
          </div>
          <button class="cm-filter__add-btn" @click="openItemModal(null)">
            <feather-icon icon="PlusIcon" size="15"/>
            <span>{{ msg('Add Item') }}</span>
          </button>
        </div>

        <b-card no-body>
          <b-table :items="filteredItems" :fields="itemFields" responsive hover show-empty
                   :empty-text="msg('No items found')" :busy="loadingItems" class="mb-0"
                   @row-clicked="openItemModal">
            <template #table-busy>
              <div class="text-center py-3"><b-spinner/></div>
            </template>
            <template #cell(image)="{ item }">
              <img v-if="item.imageUrls && item.imageUrls.length" :src="getUrl(item.imageUrls[0])"
                   class="rounded" style="width:40px;height:40px;object-fit:cover"/>
              <div v-else class="rounded d-flex align-items-center justify-content-center"
                   style="width:40px;height:40px;background:#f5f6fa">
                <feather-icon icon="ImageIcon" size="16" class="text-muted"/>
              </div>
            </template>
            <template #cell(price)="{ item }">
              {{ formatPrice(item.price, item.currency) }}
            </template>
            <template #cell(stock)="{ item }">
              <span v-if="item.stock != null">{{ item.stock }}</span>
              <span v-else class="text-muted">&#8734;</span>
            </template>
            <template #cell(branches)="{ item }">
              <span v-if="!item.branchIds || item.branchIds.length === 0" class="text-muted">{{ msg('All') }}</span>
              <span v-else>{{ item.branchIds.map(id => branchName(id)).join(', ') }}</span>
            </template>
            <template #cell(active)="{ item }">
              <b-badge :variant="item.active ? 'light-success' : 'light-secondary'">
                {{ item.active ? msg('Active') : msg('Inactive') }}
              </b-badge>
            </template>
            <template #cell(actions)="{ item }">
              <b-button variant="flat-danger" size="sm" @click.stop="onDeleteItem(item)">
                <feather-icon icon="TrashIcon" size="14"/>
              </b-button>
            </template>
          </b-table>
        </b-card>
      </b-tab>

      <!-- ─── Orders Tab ─── -->
      <b-tab>
        <div class="cm-filter mb-1">
          <div class="cm-filter__search">
            <feather-icon icon="SearchIcon" size="15" class="cm-filter__search-icon"/>
            <b-form-input v-model="orderSearch" class="cm-filter__search-input" :placeholder="msg('Search orders...')" debounce="300"/>
          </div>
          <div class="cm-filter__divider"/>
          <div class="cm-filter__select">
            <v-select v-model="orderFilterStatus" :options="statusOptions" :reduce="s=>s.value" label="label"
                      :placeholder="msg('All statuses')" :clearable="true" class="cm-filter__vs"/>
          </div>
          <div class="cm-filter__select">
            <v-select v-model="orderFilterBranch" :options="branchOptions" :reduce="b=>b.id" label="name"
                      :placeholder="msg('All branches')" :clearable="true" class="cm-filter__vs"/>
          </div>
          <button class="cm-filter__refresh-btn" @click="refreshOrders">
            <feather-icon icon="RefreshCwIcon" size="15"/>
          </button>
        </div>

        <b-card no-body>
          <b-table :items="filteredOrders" :fields="orderFields" responsive hover show-empty
                   :empty-text="msg('No orders found')" :busy="loadingOrders" class="mb-0">
            <template #table-busy>
              <div class="text-center py-3"><b-spinner/></div>
            </template>
            <template #cell(items)="{ item }">
              <span>{{ item.items.map(l => l.name).join(', ') }}</span>
            </template>
            <template #cell(total)="{ item }">
              <span class="font-weight-bolder">{{ formatPrice(item.total, item.currency) }}</span>
            </template>
            <template #cell(branch)="{ item }">
              {{ branchName(item.branchId) }}
            </template>
            <template #cell(status)="{ item }">
              <v-select :value="item.status" :options="statusOptions" :reduce="s=>s.value" label="label"
                        :clearable="false" class="select-size-sm" style="min-width:130px"
                        append-to-body
                        @input="val => changeOrderStatus(item, val)">
                <template #selected-option="{ label, value }">
                  <b-badge :variant="statusVariant(value)">{{ label }}</b-badge>
                </template>
                <template #option="{ label, value }">
                  <b-badge :variant="statusVariant(value)">{{ label }}</b-badge>
                </template>
              </v-select>
            </template>
            <template #cell(createdAt)="{ item }">
              {{ item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '' }}
            </template>
          </b-table>
        </b-card>
      </b-tab>
    </b-tabs>

    <catalog-item-modal v-model="showItemModal" :item="editingItem" @saved="refreshItems"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CatalogItemModal from '@/components/modals/catalog/CatalogItemModal.vue'

export default {
  name: 'CatalogManagement',
  components: { CatalogItemModal },
  data() {
    return {
      activeTab: 0,
      loadingItems: false,
      loadingOrders: false,
      itemSearch: '',
      itemFilterBranch: null,
      itemFilterActive: null,
      orderSearch: '',
      orderFilterStatus: null,
      orderFilterBranch: null,
      showItemModal: false,
      editingItem: null,
    }
  },
  computed: {
    ...mapGetters('catalog', ['allItems', 'allOrders']),
    ...mapGetters('data', ['getAllBranches']),
    branchOptions() { return Object.values(this.getAllBranches) },
    activeOptions() {
      return [
        { label: this.msg('Active'), value: true },
        { label: this.msg('Inactive'), value: false },
      ]
    },
    statusOptions() {
      return [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Paid', value: 'PAID' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Refunded', value: 'REFUNDED' },
      ]
    },
    stats() {
      const items = this.allItems
      const orders = this.allOrders
      return {
        totalItems: items.length,
        activeItems: items.filter(i => i.active).length,
        totalOrders: orders.length,
        pendingOrders: orders.filter(o => o.status === 'PENDING').length,
        paidOrders: orders.filter(o => o.status === 'PAID').length,
      }
    },
    itemFields() {
      return [
        { key: 'image', label: '', tdClass: 'py-50' },
        { key: 'name', label: this.msg('Name'), sortable: true },
        { key: 'price', label: this.msg('Price'), sortable: true },
        { key: 'stock', label: this.msg('Stock') },
        { key: 'branches', label: this.msg('Branches') },
        { key: 'active', label: this.msg('Status') },
        { key: 'actions', label: '', tdClass: 'text-right' },
      ]
    },
    orderFields() {
      return [
        { key: 'id', label: '#', formatter: v => v ? v.slice(-6).toUpperCase() : '' },
        { key: 'items', label: this.msg('Items') },
        { key: 'total', label: this.msg('Total'), sortable: true },
        { key: 'branch', label: this.msg('Branch') },
        { key: 'status', label: this.msg('Status') },
        { key: 'createdAt', label: this.msg('Date'), sortable: true },
      ]
    },
    filteredItems() {
      let list = [...this.allItems]
      if (this.itemSearch) {
        const q = this.itemSearch.toLowerCase()
        list = list.filter(i => (i.name || '').toLowerCase().includes(q))
      }
      if (this.itemFilterBranch != null) {
        list = list.filter(i => !i.branchIds || i.branchIds.length === 0 || i.branchIds.includes(this.itemFilterBranch))
      }
      if (this.itemFilterActive != null) {
        list = list.filter(i => i.active === this.itemFilterActive)
      }
      return list
    },
    filteredOrders() {
      let list = [...this.allOrders]
      if (this.orderSearch) {
        const q = this.orderSearch.toLowerCase()
        list = list.filter(o => o.id.toLowerCase().includes(q) || o.items.some(l => (l.name || '').toLowerCase().includes(q)))
      }
      if (this.orderFilterStatus) {
        list = list.filter(o => o.status === this.orderFilterStatus)
      }
      if (this.orderFilterBranch != null) {
        list = list.filter(o => o.branchId === this.orderFilterBranch)
      }
      return list
    },
  },
  created() {
    this.refreshItems()
    this.refreshOrders()
  },
  methods: {
    ...mapActions('catalog', { _loadItems: 'loadItems', _loadOrders: 'loadOrders', _deleteItem: 'deleteItem', _updateOrderStatus: 'updateOrderStatus' }),
    branchName(id) {
      const b = this.getAllBranches[id]
      return b ? b.name : ('#' + id)
    },
    formatPrice(v, cur) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: cur || 'EUR' }).format(v || 0)
    },
    statusVariant(s) {
      const m = { PAID: 'light-success', PENDING: 'light-warning', CANCELLED: 'light-danger', REFUNDED: 'light-info' }
      return m[s] || 'light-secondary'
    },
    async refreshItems() {
      this.loadingItems = true
      try { await this._loadItems({}) } finally { this.loadingItems = false }
    },
    async refreshOrders() {
      this.loadingOrders = true
      try { await this._loadOrders({}) } finally { this.loadingOrders = false }
    },
    openItemModal(item) {
      this.editingItem = item || null
      this.showItemModal = true
    },
    async onDeleteItem(item) {
      const ok = await this.$swal({ title: this.msg('Delete item?'), text: item.name, icon: 'warning', showCancelButton: true, confirmButtonText: this.msg('Delete') })
      if (ok.isConfirmed) {
        await this._deleteItem(item.id)
        this.refreshItems()
      }
    },
    async changeOrderStatus(order, newStatus) {
      try {
        await this._updateOrderStatus({ orderId: order.id, status: newStatus })
        this.$bvToast.toast(this.msg('Order status updated successfully.'), {
          title: this.msg('Status Updated'),
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })
      } catch (e) {
        this.$bvToast.toast(this.msg('Failed to update order status.'), {
          title: this.msg('Error'),
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss">
.catalog-hero {
  position: relative; border-radius: 12px; overflow: hidden; min-height: 120px;
  &__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #7367f0 0%, #9e95f5 50%, #ce9ffc 100%); }
  &__content { position: relative; z-index: 1; padding: 1.5rem 1.75rem; color: #fff; }
  &__title { color: #fff; font-size: 1.4rem; font-weight: 700; }
  &__subtitle { color: rgba(255,255,255,.75); font-size: .9rem; }
  &__stat {
    display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px;
    background: rgba(255,255,255,.18); color: #fff; font-size: .82rem; backdrop-filter: blur(4px);
    &--success { background: rgba(40,199,111,.25); }
    &--warning { background: rgba(255,159,67,.25); }
    &--info { background: rgba(0,207,232,.25); }
    &--secondary { background: rgba(255,255,255,.12); }
  }
  &__illustration { position: absolute; right: 1rem; top: 50%; transform: translateY(-50%); height: 100px; opacity: .6; }
}

/* ───── Custom Tabs ───── */
.cm-nav {
  &__tabs {
    display: inline-flex; gap: 6px; padding: 3px;
    background: #f0f0f5; border-radius: 12px;
  }
  &__tab {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 18px; border-radius: 9px; border: none;
    background: transparent; color: #8e8ea0; cursor: pointer;
    font-weight: 600; font-size: .82rem;
    transition: all .2s ease;
    &:hover { color: #5e5873; background: rgba(255,255,255,.6); }
    &--active {
      background: #fff; color: #7367f0;
      box-shadow: 0 1px 6px rgba(34,41,47,.06);
    }
  }
  &__tab-count {
    display: inline-flex; align-items: center; justify-content: center;
    min-width: 20px; height: 20px; padding: 0 5px;
    border-radius: 20px; font-size: .68rem; font-weight: 700;
    background: rgba(115,103,240,.1); color: #7367f0;
    line-height: 1;
  }
  &__tab--active &__tab-count {
    background: #7367f0; color: #fff;
  }
}

/* ───── Filter bar ───── */
.cm-filter {
  display: flex; align-items: center; gap: 0;
  background: #fff; border-radius: 12px; padding: 4px 6px;
  box-shadow: 0 1px 3px rgba(34,41,47,.04);
  border: 1px solid #ededf2;
  flex-wrap: wrap;
  &__search {
    position: relative; flex: 1; min-width: 140px;
  }
  &__search-icon {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #b9b9c3; z-index: 1;
  }
  &__search-input {
    padding-left: 34px; border: none; background: transparent; border-radius: 8px;
    font-size: .84rem; height: 38px;
    &:focus { box-shadow: none; background: #f8f8fc; }
  }
  &__divider {
    width: 1px; height: 20px; background: #e8e8ee; margin: 0 6px; flex-shrink: 0;
    @media (max-width: 767px) { display: none; }
  }
  &__select {
    min-width: 130px; flex: 0 1 170px;
    &--sm { flex: 0 1 110px; min-width: 90px; }
  }
  &__vs .vs__dropdown-toggle {
    border: none; background: transparent; padding: 2px 6px; min-height: 38px;
    border-radius: 8px;
    &:hover { background: #f8f8fc; }
  }
  &__vs .vs__search { font-size: .82rem; }
  &__vs .vs__selected { font-size: .82rem; }
  &__add-btn {
    display: flex; align-items: center; gap: 5px; margin-left: auto;
    padding: 7px 14px; border-radius: 9px; border: none;
    background: linear-gradient(135deg, #7367f0, #9e95f5); color: #fff;
    font-weight: 600; font-size: .8rem; cursor: pointer; flex-shrink: 0;
    transition: all .2s ease;
    &:hover {
      box-shadow: 0 4px 14px rgba(115,103,240,.35);
      transform: translateY(-1px);
    }
  }
  &__refresh-btn {
    width: 36px; height: 36px; border-radius: 9px; border: none;
    background: transparent; color: #b4b7bd; cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    margin-left: auto;
    transition: all .2s;
    &:hover { background: #f4f4f8; color: #7367f0; }
  }
}

/* ───── Dark mode ───── */
.dark-layout {
  .cm-nav__tabs { background: #1e2a3e; }
  .cm-nav__tab {
    color: #5a6078;
    &:hover { color: #b4b7bd; background: rgba(255,255,255,.04); }
    &--active { background: #283046; color: #7367f0; box-shadow: 0 1px 6px rgba(0,0,0,.15); }
  }
  .cm-nav__tab-count { background: rgba(115,103,240,.15); color: #7367f0; }
  .cm-nav__tab--active .cm-nav__tab-count { background: #7367f0; color: #fff; }
  .cm-filter {
    background: #283046; border-color: rgba(255,255,255,.06);
    box-shadow: 0 1px 3px rgba(0,0,0,.15);
  }
  .cm-filter__search-input { color: #d0d2d6; &:focus { background: rgba(255,255,255,.04); } }
  .cm-filter__search-icon { color: #4b5572; }
  .cm-filter__divider { background: rgba(255,255,255,.06); }
  .cm-filter__vs .vs__dropdown-toggle:hover { background: rgba(255,255,255,.04); }
  .cm-filter__refresh-btn { color: #4b5572; &:hover { background: rgba(255,255,255,.04); color: #7367f0; } }
}
</style>
